import type { StorefrontPage as PageDto } from '@erp/shared';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { Skeleton } from '../../components/shadcn/index.js';
import { RTE_CONTENT_CSS } from '../../components/ui/index.js';
import { publicApi } from '../../lib/publicApi.js';
import { sanitizeHtml } from '../../lib/sanitizeHtml.js';

import { Breadcrumbs } from './Breadcrumbs.js';

/** Roomier, more readable prose for the public content page. Scoped with a
 *  second class so these rules win over the shared editor `.rte-content` CSS. */
const PAGE_PROSE_CSS = `
.sf-page-prose.rte-content { font-size: 1rem; line-height: 1.75; color: var(--color-fg); }
.sf-page-prose.rte-content > :first-child { margin-top: 0; }
.sf-page-prose.rte-content p { margin: 0 0 1.1rem; }
.sf-page-prose.rte-content h2 { font-size: 1.5rem; font-weight: 800; letter-spacing: -0.01em; margin: 2rem 0 0.75rem; }
.sf-page-prose.rte-content h3 { font-size: 1.2rem; font-weight: 700; margin: 1.6rem 0 0.6rem; }
.sf-page-prose.rte-content ul, .sf-page-prose.rte-content ol { margin: 0 0 1.1rem; padding-inline-start: 1.5em; }
.sf-page-prose.rte-content li { margin: 0.35rem 0; }
.sf-page-prose.rte-content blockquote { margin: 1.25rem 0; padding-inline-start: 1rem; }
.sf-page-prose.rte-content img { max-width: 100%; height: auto; border-radius: 0.75rem; margin: 1.25rem 0; }
`;

/** Public content page (/s/:slug/p/:pageSlug). Body is sanitized server-side
 *  AND again here before render (defense-in-depth). */
export function StorefrontPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug = '', pageSlug = '' } = useParams();
  const { site } = useStorefront();
  const [page, setPage] = useState<PageDto | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'notfound'>('loading');

  useEffect(() => {
    setStatus('loading');
    void publicApi()
      .get<PageDto>(`/${slug}/pages/${encodeURIComponent(pageSlug)}`)
      .then((r) => { setPage(r.data); setStatus('ok'); })
      .catch(() => setStatus('notfound'));
  }, [slug, pageSlug, i18n.language]);

  if (status === 'loading') return <Skeleton className="h-64 w-full" />;
  if (status === 'notfound' || !page) {
    return <div className="py-16 text-center text-fg-muted">{t('store.pageNotFound')}</div>;
  }

  return (
    <div className="mx-auto max-w-3xl">
      <style>{RTE_CONTENT_CSS}</style>
      <style>{PAGE_PROSE_CSS}</style>
      <Helmet>
        <title>{`${page.seoTitle ?? page.title} · ${site.storeName}`}</title>
        {page.seoDescription && <meta name="description" content={page.seoDescription} />}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={page.seoTitle ?? page.title} />
        {page.seoDescription && <meta property="og:description" content={page.seoDescription} />}
      </Helmet>

      <Breadcrumbs
        items={[
          { label: t('store.home.crumb'), to: `/s/${slug}` },
          { label: page.title },
        ]}
      />

      {/* Designed page header — brand-tinted band with the page title. */}
      <header className="mt-4 overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-brand/10 via-bg-subtle to-bg px-6 py-9 shadow-sm sm:px-9 sm:py-11">
        <h1 className="text-3xl font-bold tracking-tight text-fg sm:text-4xl">{page.title}</h1>
        <div className="mt-3 h-1 w-14 rounded-full bg-brand/70" aria-hidden />
      </header>

      {/* Readable, constrained prose column. */}
      <article
        className="sf-page-prose rte-content mt-8"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.bodyHtml) }}
      />
    </div>
  );
}
